import deepmerge from 'deepmerge';

import gameIds from 'a/hub/data/GameIds';
import SearchlightDispatcher, { SEARCHLIGHT_EVENT_TYPE } from 'lib/SearchlightDispatcher';
import * as AppSessionActions from 'a/actions/appSessionActions';
import { SEARCHLIGHT_ACTION_TYPE } from 'a/actions/searchlightActionCreators';
import { logOutUser } from 'a/api/logout';
import { LANGUAGES, PRODUCT_READING, PRODUCT_LECTURA, GAME_MODE } from 'lib/constants';
import { hasTimedOut, getReportingGameId } from 'root/lib/utilityFunctions';
import { verifyGameLanguage } from 'root/lib/StaticAssetsManifest';

import Env from '../../lib/Env';
import { throwCustomAquaError } from '../components/GlobalErrorHandler/actions';
import { ERROR_STRINGS } from '../components/GlobalErrorHandler/constants';
import { GRADES, SEASON } from '../hub/data/constants';
import { devUpdateGameDetails, writeResults } from './resultsApiActions';
import { postToObservationApi } from './observationApiActions';
import { setSkillScanAggregateScore, setSkillScanSessionData } from '../skillScan/redux/actions';
import {
  getGameMode,
  getGameFailures,
  getAssignmentLinkLevels,
  getIsDeepLinkELA,
  getDeepLinkProgram,
} from '../hub/selectors';

export const eventTypesWithDeepLinkInfo = [
  SEARCHLIGHT_EVENT_TYPE.MINIGAME_LAUNCHED,
  SEARCHLIGHT_EVENT_TYPE.LOAD_GAME,
  SEARCHLIGHT_EVENT_TYPE.SELECTION,
  SEARCHLIGHT_EVENT_TYPE.ROUND_START,
  SEARCHLIGHT_EVENT_TYPE.LEVEL_COMPLETE,
  SEARCHLIGHT_EVENT_TYPE.ROUND_COMPLETE,
];

const DATA_SOURCES = {
  BOOST: 'boost',
  CKLA: 'ckla',
  ELA: 'ela',
};

const getEMDataSource = (isQuest, isDeepLinkELA) => {
  if (isQuest) {
    return DATA_SOURCES.BOOST;
  }
  if (isDeepLinkELA) {
    return DATA_SOURCES.ELA;
  }
  return DATA_SOURCES.CKLA;
};

let searchlightDispatcher;
let savedItemRecords = [];
let responseIndex = 0;
let gameLevelFailCount = 0;

// Searchlight Middleware -  when a redux action is called, it goes through this middleware first.
// If the type matches one of the Searchlight actions, we call the appropriate
// Searchlight methods here before passing the action on to the rest of Redux

// Middleware info/tutorial:
// https://www.codementor.io/@vkarpov/beginner-s-guide-to-redux-middleware-du107uyud

const SearchlightMiddleware = ({ dispatch, getState }) => next => async (action) => {
  const state = getState();
  const {
    persistStateReducer: persistState,
    questWorkflowReducer: {
      language,
      isQuestInDemoMode,
      questSessionId,
      gameLevelConceptMap,
    },
    workshopReducer: {
      questSessionId: questSessionIdS3,
    },
    selectedActivity: {
      isQuestWorkflow,
      params,
    },
    assignmentLink: {
      currentAssignmentId,
    },
    appSessionReducer: {
      lastCallTime,
      product,
    },
    skillScan: {
      redirectToSkillScan,
      measuresIds = [],
      skillScanSessionId,
      skillScanSessionData,
    },
  } = state;

  const gameMode = getGameMode(state);
  const program = getDeepLinkProgram(state);
  const isAssignmentLinkMode = gameMode === GAME_MODE.ASSIGNMENT_LINK;
  const isDeepLinkELA = isAssignmentLinkMode && getIsDeepLinkELA(state);
  const isGameModeForEM = isQuestWorkflow || isAssignmentLinkMode;

  const localQuestSessionId = persistState.season === SEASON.GRADE_4_5 ? questSessionIdS3 : questSessionId;

  const isLectura = product === PRODUCT_LECTURA;
  const isLecturaSkillScan = isLectura && redirectToSkillScan;

  let { payload = {} } = action; // payload can be mutated by different paths through searchlight middleware

  switch (action.type) {
    // <-------------------------PRE-APP LOAD ACTIONS------------------------->
    case SEARCHLIGHT_ACTION_TYPE.INIT_SEARCHLIGHT_DISPATCHER: {
      searchlightDispatcher = new SearchlightDispatcher(action.payload);
      break;
    }

    case SEARCHLIGHT_ACTION_TYPE.SET_USER_ID:
      searchlightDispatcher.setUserId(action.payload);
      break;

    case SEARCHLIGHT_ACTION_TYPE.SET_IDENTITY:
      searchlightDispatcher.setIdentity(action.payload);
      break;

    case SEARCHLIGHT_ACTION_TYPE.SET_SOURCE_ID:
      searchlightDispatcher.setSourceId(action.payload);
      break;

    case SEARCHLIGHT_ACTION_TYPE.REPORT_LOGIN_EVENT: {
      const { status, data } = action.payload;
      await searchlightDispatcher.reportLoginEvent(status, data);
      break;
    }

    case SEARCHLIGHT_ACTION_TYPE.REPORT_METRIC: {
      const { metricId, metricType, value, product: metricProduct } = action.payload;
      searchlightDispatcher.reportMetric(metricId, metricType, value, metricProduct);
      break;
    }

    case SEARCHLIGHT_ACTION_TYPE.SET_SKILL_MAP: {
      searchlightDispatcher.setSkillMap(action.payload);
      break;
    }

    // <-------------------------POST-APP LOAD ACTIONS------------------------->
    case SEARCHLIGHT_ACTION_TYPE.REPORT_APP_LOADED:
      searchlightDispatcher.setIdentity({
        ...persistState,
        language: isLectura ? LANGUAGES.ES : language,
      });
      postSearchlightEvent(dispatch, {
        application: { eventType: SEARCHLIGHT_EVENT_TYPE.APP_LOADED },
        session: { product },
      });
      break;

    case SEARCHLIGHT_ACTION_TYPE.DISPATCH_EVENT:
    case SEARCHLIGHT_ACTION_TYPE.SEARCHLIGHT_DISPATCH_EVENT: {
      if (hasTimedOut(lastCallTime)) {
        logOutUser();
        return next(action);
      }

      let deepLinkData = {};

      if (eventTypesWithDeepLinkInfo.includes(payload.application.eventType)) {
        const { isLink, deepLinkConfig } = params || {};
        deepLinkData = {
          isDeepLinkMode: !!isLink,
          ...(!!isLink && { deepLinkConfig }),
        };
      }

      const searchlightData = state.appSessionReducer.searchlight || {};
      const { level, roundSessionId } = searchlightData;

      // extend payload object with Quest workflow flags from state
      payload = deepmerge({
        application: {
          details: {
            gameMode,
            isQuestWorkflow,
            isQuestInDemoMode,
            ...deepLinkData,
            ...(level && { level }),
          },
        },
        session: { product, program },
      }, payload);

      switch (payload.application.eventType) {
        case SEARCHLIGHT_EVENT_TYPE.LEVEL_COMPLETE: {
          // Post results to Observation API
          dispatch(postToObservationApi({
            userData: searchlightDispatcher.identity,
            gameData: {
              ...payload.application,
              gameId: getReportingGameId(payload.application.gameId, product, program),
            },
            sessionData: {
              product,
              questSessionId: localQuestSessionId,
            },
          }));
          // add writtenByAPI flag to payload
          payload = deepmerge(payload, { application: { writtenByAPI: true } });

          // AQUA-27813 use the previous round session id so that data scientists
          // can tie the LEVEL_COMPLETE event to round data
          payload.isUsePreviousRoundSessionId = true;

          postSearchlightEvent(dispatch, payload);
          break;
        }

        case SEARCHLIGHT_EVENT_TYPE.EXIT_GAME:
          // postSearchlightEvent and set a new game session Id
          postSearchlightEvent(dispatch, payload);
          searchlightDispatcher.setGameSessionId();
          break;

        case SEARCHLIGHT_EVENT_TYPE.ROUND_START: {
          const {
            searchlight = {},
            application: {
              gameId,
              details,
            },
          } = payload;

          // update Embedded Measurement vars
          savedItemRecords = [];
          responseIndex = 0;
          const failureCount = getGameFailures(state) || {};
          gameLevelFailCount = failureCount[`${gameId}_${details.level - 1}`] || 0; // failure levels are 0-indexed

          // add language to application details before dispatching ROUND_START
          let voLanguage = language !== LANGUAGES.EN && verifyGameLanguage(gameId, language) ? language : LANGUAGES.EN;
          if (isLectura) {
            voLanguage = LANGUAGES.ES;
          }
          payload = deepmerge(payload, {
            application: {
              details: {
                language: voLanguage,
              },
            },
          });

          // set a new roundSessionId then postSearchlightEvent (if none provided in payload, a new ID is generated)
          searchlightDispatcher.setRoundSessionId(searchlight.roundSessionId);
          if (redirectToSkillScan && !searchlightDispatcher.skillScanSessionId) {
            searchlightDispatcher.setSkillScanSessionId(skillScanSessionId);
            if (skillScanSessionData.roundStartTimestamp) {
              searchlightDispatcher.setSkillScanSessionData(skillScanSessionData);
            } else if (isLecturaSkillScan) {
              // will be used as TimestampStarted in /writeResult for skill_scan_aggregate_es
              searchlightDispatcher.setSkillScanStartTimestamp();
            }
          }

          postSearchlightEvent(dispatch, payload);

          dispatch({
            type: SEARCHLIGHT_ACTION_TYPE.SET_SEARCHLIGHT_DATA,
            payload: {
              roundSessionId: searchlightDispatcher.roundSessionId,
              roundStartTimestamp: searchlightDispatcher.lastMessageTimestamp,
              level: details.level,
              ...(redirectToSkillScan && {
                skillScanSessionId: searchlightDispatcher.skillScanSessionId,
                skillScanSessionData: searchlightDispatcher.skillScanSessionData,
              }),
            },
          });

          // For multi-measure skill scans, persist skillScanSessionId to redux immediately on ROUND_START for proper saving
          if (redirectToSkillScan && measuresIds.length > 1) {
            dispatch(setSkillScanSessionData(
              searchlightDispatcher.skillScanSessionId,
              searchlightDispatcher.skillScanSessionData,
            ));
          }

          break;
        }

        case SEARCHLIGHT_EVENT_TYPE.SELECTION: {
          const {
            application: {
              writtenToAPI,
              details: {
                isCorrect,
                uuid,
                skipEvent = false,
              },
            },
          } = payload;

          // For embedded measurement
          if (product === PRODUCT_READING && isGameModeForEM && writtenToAPI) {
            savedItemRecords.push({
              EmId: uuid,
              ResponseIndex: responseIndex++, // order of student's responses to items per level
              Score: +isCorrect, // needs to be floating point
            });
          }

          if (!skipEvent) {
            postSearchlightEvent(dispatch, payload);
          }
          break;
        }

        case SEARCHLIGHT_EVENT_TYPE.ROUND_COMPLETE: {
          const {
            application: {
              writtenByAPI,
              writtenToAPI,
              details: {
                Records,
                isEarlyExit,
              },
            },
          } = payload;

          // If level not complete (isEarlyExit), send EARLY_EXIT instead of ROUND_COMPLETE
          if (isEarlyExit) {
            payload = deepmerge(payload, { application: { eventType: SEARCHLIGHT_EVENT_TYPE.EARLY_EXIT } });
          }

          postSearchlightEvent(dispatch, payload);

          let sessionDataSyncedEarly = false;

          if (writtenByAPI) {
            const userData = searchlightDispatcher.identity;
            const gameData = payload.application;
            const { skillsByGameId } = state.powerReducer;
            const reportingGameId = getReportingGameId(gameData.gameId, product, program);
            const skillData = skillsByGameId?.[reportingGameId]?.[gameData.details.level];
            const gameConcept = gameLevelConceptMap?.[`${reportingGameId}-${gameData.details.level}`] || {};
            const isValidEMGame =
              gameConcept.concept &&
              gameConcept.concept !== 'na' &&
              [persistState.grade, null].includes(gameConcept.grade);
            const shouldSendItemsData =
              !!savedItemRecords.length && product === PRODUCT_READING
              && isGameModeForEM && writtenToAPI && isValidEMGame;
            const assignmentLinkLevels = getAssignmentLinkLevels(state);

            const isLastSkillScanMeasure = reportingGameId?.includes(measuresIds[measuresIds.length - 1]);
            const isAggregateResult = reportingGameId?.includes(gameIds.SKILL_SCAN_AGGREGATE); // lectura grades K-1
            const needsGrowthResult = redirectToSkillScan && (
              !isLectura ||
              (isLectura && (![GRADES.KINDERGARTEN, GRADES.GRADE_1].includes(persistState.grade) || isAggregateResult))
            );

            const resultItemsData = shouldSendItemsData ? {
              IsBoostDeepLink: isAssignmentLinkMode,
              ...(isAssignmentLinkMode && {
                DeepLinkMetadata: {
                  AssignmentId: currentAssignmentId,
                  TotalLevelCorrectQuestions: gameData.details.questionsCorrect,
                  TotalLevelIncorrectQuestions: gameData.details.questionCount - gameData.details.questionsCorrect,
                  TotalAssignmentQuestions: assignmentLinkLevels.length * gameData.details.questionCount,
                },
              }),
              DataSource: getEMDataSource(isQuestWorkflow, isDeepLinkELA),
              UserId: userData.userId,
              GameId: reportingGameId,
              Level: gameData.details.level,
              RoundSessionId: roundSessionId,
              Records: Records ?? savedItemRecords,
              Attempt: gameLevelFailCount + 1,
            } : null;

            if (isLecturaSkillScan) {
              const gameDetails =
                Env.isDevEnv && payload.application.details.devDetails
                  ? devUpdateGameDetails(payload.application.details)
                  : payload.application.details;
              dispatch(setSkillScanAggregateScore(gameDetails));

              // will be used as TimestampCompleted in /writeResult for skill_scan_aggregate_es
              if (isLastSkillScanMeasure) {
                searchlightDispatcher.setSkillScanCompleteTimestamp();
                // persist before the /writeResult await below, so a slow or failed write-result
                // can't let markSkillScanAsComplete's save race ahead of this timestamp landing in redux
                dispatch(setSkillScanSessionData(
                  searchlightDispatcher.skillScanSessionId,
                  searchlightDispatcher.skillScanSessionData,
                ));
                sessionDataSyncedEarly = true;
              }
            }

            await dispatch(writeResults({
              userData,
              gameData: {
                ...gameData,
                gameId: reportingGameId,
              },
              sessionData: {
                ...searchlightData,
                roundCompleteTimestamp: searchlightDispatcher.lastMessageTimestamp,
              },
              skillScan: {
                needsGrowthResult,
                isAggregateResult,
                skillScanSessionId: searchlightDispatcher.skillScanSessionId,
                skillScanSessionData: {
                  ...searchlightData,
                  ...searchlightDispatcher.skillScanSessionData,
                },
              },
              season: state.questWorkflowReducer.season,
              isDemoMode: state.questWorkflowReducer.isQuestInDemoMode,
              product,
              skillData,
            }, resultItemsData));
          }

          if (redirectToSkillScan && !sessionDataSyncedEarly) {
            // update skill scan redux state before searchlight data clears
            dispatch(setSkillScanSessionData(
              searchlightDispatcher.skillScanSessionId,
              searchlightDispatcher.skillScanSessionData,
            ));
          }

          dispatch({ type: SEARCHLIGHT_ACTION_TYPE.CLEAR_SEARCHLIGHT_DATA });
          break;
        }

        case SEARCHLIGHT_EVENT_TYPE.SETTING:
          if (payload.application.details.key === 'product' && payload.application.details.value === PRODUCT_LECTURA) {
            searchlightDispatcher.setIdentityLanguage(LANGUAGES.ES);
          }
          postSearchlightEvent(dispatch, payload);
          break;
        case SEARCHLIGHT_EVENT_TYPE.LOG_OUT:
          postSearchlightEvent(dispatch, payload).finally(logOutUser);
          break;

        default:
          postSearchlightEvent(dispatch, payload);
          break;
      }
      break;
    }

    case SEARCHLIGHT_ACTION_TYPE.SEARCHLIGHT_CHANGE_GAME_SESSION:
      searchlightDispatcher.setGameSessionId();
      break;
    case SEARCHLIGHT_ACTION_TYPE.SET_QUEST_SESSION_ID:
      searchlightDispatcher.setQuestSessionId(payload.newQuestSessionId || localQuestSessionId);
      break;
    case SEARCHLIGHT_ACTION_TYPE.CLEAR_QUEST_SESSION_ID:
      searchlightDispatcher.setQuestSessionId();
      break;
    default:
      break;
  }

  // update action with final payload value before passing back to redux
  const updatedAction = {
    ...action,
    payload,
  };

  // pass action back to redux
  return next(updatedAction);
};

let searchlightFailCount = 0;

const postSearchlightEvent = (dispatch, payload) => {
  if (searchlightDispatcher) {
    return searchlightDispatcher.dispatchEvent(payload)
      .then(() => {
        searchlightFailCount = 0;
        // reset the session timer for every successful searchlight call
        dispatch(AppSessionActions.resetSessionTimer());
      })
      .catch((error) => {
        searchlightFailCount += 1;
        if (searchlightFailCount >= 15) {
          console.warn('you have exceeded the max amount of failed reporting calls');
          dispatch(throwCustomAquaError({
            titleText: ERROR_STRINGS.uhOh,
            secondaryText: ERROR_STRINGS.checkYourInternetConnection,
            showGamesButton: false,
            showHomeButton: true,
            forceLogout: false,
            errorName: error.name,
            errorMessage: error.message,
            category: 'API: searchlight',
            data: 'API: searchlight',
            metricId: 'api',
          }));
        }
        throw error;
      });
  }
  return Promise.reject(new Error('Searchlight Event Skipped: You are not properly logged in'));
};

export default SearchlightMiddleware;
